import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Logo from '../components/brand/Logo';
import Icon from '../components/brand/Icon';
import './HomePage.css';

const HomePage = () => {
  return (
    <div className="home-page">
      <Navbar />

      {/* Hero */}
      <section className="hero">
        <div className="container">
          <div className="hero-content">
            <div className="hero-badge">Parking Payments and Enforcement for RGV Businesses</div>
            <h1 className="hero-title">
              Protect Customer Parking and{' '}
              <span className="hero-title-accent">Turn Unauthorized Parking Into Revenue</span>
            </h1>
            <p className="hero-subtitle">
              RGV Smart Pay gives parking lot owners QR payments, customer validation, and fast
              license plate lookup in one simple platform.
            </p>
            <div className="hero-actions">
              <Link to="/business-signup" className="btn btn-primary btn-lg">
                Get Started
              </Link>
              <Link to="/admin/signin" className="btn btn-outline btn-lg">
                Log In
              </Link>
            </div>
            <p className="hero-note">Built for lot owners, property managers, venues, restaurants, and plazas.</p>
          </div>
          <div className="hero-visual">
            <div className="hero-card">
              <div className="hero-card-header">
                <span className="hero-card-dot green"></span>
                <span className="hero-card-label">Permit Purchased</span>
              </div>
              <div className="hero-card-amount">$5.00</div>
              <div className="hero-card-meta">Plate ABC1234 · Sample Parking Lot</div>
              <div className="hero-card-footer">
                <span className="badge badge-success">Valid</span>
                <span className="hero-card-method">Visa ···· 4242</span>
              </div>
            </div>
            <div className="hero-stat-row">
              <div className="hero-stat">
                <div className="hero-stat-value">$5</div>
                <div className="hero-stat-label">Sample flat permit</div>
              </div>
              <div className="hero-stat">
                <div className="hero-stat-value">30s</div>
                <div className="hero-stat-label">Time to pay</div>
              </div>
              <div className="hero-stat">
                <div className="hero-stat-value">1</div>
                <div className="hero-stat-label">Simple platform</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* For parking lot owners */}
      <section className="features-section">
        <div className="container">
          <div className="section-header">
            <h2>For Parking Lot Owners</h2>
            <p>Give every visitor a fair, fast option and keep control of your lot without confrontation.</p>
          </div>
          <div className="features-grid">
            {[
              { icon: 'qr', title: 'Accept QR payments', desc: 'Post a sign with a QR code. Visitors pay a flat permit fee from their phone in seconds.' },
              { icon: 'check', title: 'Validate real customers', desc: 'Paying customers validate their parking for free using a code from their receipt.' },
              { icon: 'search', title: 'Look up plates fast', desc: 'Staff check any license plate to confirm a valid permit or customer validation.' },
              { icon: 'dollar', title: 'Capture lost revenue', desc: 'Turn unauthorized parking into a steady, fair revenue stream instead of a towing dispute.' },
            ].map(f => (
              <div className="feature-card" key={f.title}>
                <div className="feature-icon"><Icon name={f.icon} size={26} /></div>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="how-section">
        <div className="container">
          <div className="section-header">
            <h2>How It Works</h2>
            <p>A simple system that keeps your customers happy and your lot under control.</p>
          </div>
          <div className="steps-row">
            {[
              { n: '1', title: 'Put up the sign', desc: 'We provide a printable QR sign for your lot. One-time setup.' },
              { n: '2', title: 'Customers validate free', desc: 'Paying customers enter a validation code from their receipt at no cost.' },
              { n: '3', title: 'Everyone else pays', desc: 'Other visitors pay a flat permit fee from their phone. No confrontation needed.' },
            ].map((s, i) => (
              <React.Fragment key={s.n}>
                <div className="step-box">
                  <div className="step-num">{s.n}</div>
                  <h3>{s.title}</h3>
                  <p>{s.desc}</p>
                </div>
                {i < 2 && <div className="step-arrow"><Icon name="link" size={20} /></div>}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* Customer validation */}
      <section className="features-section">
        <div className="container">
          <div className="split-row">
            <div className="split-text">
              <div className="split-icon"><Icon name="check" size={28} /></div>
              <h2>Customer Validation</h2>
              <p>
                Your paying customers should never pay to park. With RGV Smart Pay, customers enter
                their license plate and a validation code from their receipt to confirm free parking
                in seconds. Validation is handled on their own phone, so your staff stays focused on
                running the business.
              </p>
              <Link to="/spacemonkey/validate" className="btn btn-outline">See the validation page</Link>
            </div>
            <div className="split-visual">
              <div className="mini-card">
                <div className="mini-card-row"><span>License Plate</span><strong>ABC1234</strong></div>
                <div className="mini-card-row"><span>Validation Code</span><strong>SM-12345</strong></div>
                <div className="mini-card-status success">
                  <Icon name="check" size={18} /> Parking validated. No payment required.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Enforcement lookup */}
      <section className="how-section">
        <div className="container">
          <div className="split-row reverse">
            <div className="split-text">
              <div className="split-icon"><Icon name="search" size={28} /></div>
              <h2>Enforcement Lookup</h2>
              <p>
                Before any enforcement action, staff can look up a license plate to see permit status
                instantly. Take a photo of the plate or type it in to confirm whether a vehicle has a
                valid permit, a customer validation, an expired permit, or no permit at all.
              </p>
              <Link to="/enforcement" className="btn btn-outline">Open enforcement lookup</Link>
            </div>
            <div className="split-visual">
              <div className="mini-card">
                <div className="mini-card-plate">AMC5555</div>
                <div className="mini-card-status danger">
                  <Icon name="ban" size={18} /> No permit found
                </div>
                <div className="mini-card-note">Ask driver to pay or proceed with lot policy.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Revenue examples */}
      <section className="features-section">
        <div className="container">
          <div className="section-header">
            <h2>Revenue Examples</h2>
            <p>Sample flat-fee permit examples at $5 per permit. Actual results vary by lot and traffic.</p>
          </div>
          <div className="revenue-table">
            {[
              { permits: 15, daily: 75, monthly: 2250 },
              { permits: 30, daily: 150, monthly: 4500 },
              { permits: 60, daily: 300, monthly: 9000 },
            ].map(row => (
              <div className="revenue-row" key={row.permits}>
                <div className="revenue-cell">
                  <div className="revenue-label">Permits per day</div>
                  <div className="revenue-value">{row.permits}</div>
                </div>
                <div className="revenue-divider">at $5</div>
                <div className="revenue-cell">
                  <div className="revenue-label">Daily revenue</div>
                  <div className="revenue-value green">${row.daily}</div>
                </div>
                <div className="revenue-divider">x 30 days</div>
                <div className="revenue-cell">
                  <div className="revenue-label">Monthly revenue</div>
                  <div className="revenue-value primary">${row.monthly.toLocaleString()}/mo</div>
                </div>
              </div>
            ))}
          </div>
          <p className="revenue-disclaimer">
            These are sample flat-fee permit examples for illustration. Set your own permit price for your lot.
          </p>
        </div>
      </section>

      {/* Why businesses choose RGV Smart Pay */}
      <section className="how-section">
        <div className="container">
          <div className="section-header">
            <h2>Why Businesses Choose RGV Smart Pay</h2>
            <p>One platform for payments, validation, and enforcement.</p>
          </div>
          <div className="features-grid">
            {[
              { icon: 'bolt', title: 'Fast to launch', desc: 'Post a QR sign and start accepting parking payments the same day.' },
              { icon: 'shield', title: 'Fair and professional', desc: 'A clear paid option keeps interactions calm and respectful for everyone.' },
              { icon: 'card', title: 'Secure payments', desc: 'Card payments are processed securely through Stripe. No card data is stored.' },
              { icon: 'building', title: 'Built for local business', desc: 'Designed for RGV lot owners, property managers, venues, restaurants, and plazas.' },
            ].map(f => (
              <div className="feature-card" key={f.title}>
                <div className="feature-icon"><Icon name={f.icon} size={26} /></div>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="cta-banner">
        <div className="container">
          <h2>Ready to protect your parking lot?</h2>
          <p>Set up payments, validation, and enforcement in one platform.</p>
          <div className="cta-banner-actions">
            <Link to="/business-signup" className="btn btn-primary btn-lg">Get Started</Link>
            <Link to="/contact" className="btn btn-outline btn-lg" style={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)' }}>Talk to Sales</Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="home-footer">
        <div className="container">
          <div className="footer-grid">
            <div className="footer-brand">
              <Logo size="md" mono />
              <p>Parking payments and enforcement for Rio Grande Valley businesses.</p>
              <p className="footer-location">Rio Grande Valley, Texas</p>
            </div>
            <div className="footer-col">
              <h4>Platform</h4>
              <Link to="/business-signup">Business Portal</Link>
              <Link to="/admin/signin">Log In</Link>
            </div>
            <div className="footer-col">
              <h4>Enforcement</h4>
              <Link to="/enforcement">Plate Lookup</Link>
              <Link to="/enforcement/scan">Scan Plate</Link>
            </div>
            <div className="footer-col">
              <h4>Business</h4>
              <Link to="/pricing">Pricing</Link>
              <Link to="/contact">Contact</Link>
              <Link to="/admin/signin">Login</Link>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; {new Date().getFullYear()} RGV Smart Pay. All rights reserved.</p>
            <p>Payments powered by Stripe. Secured with SSL.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
